import {ChangeEvent, ReactNode, useMemo, useRef, useState} from 'react';
import ArrButton from '../../../../../common/arr-button/ArrButton';
import ArrModal from '../../../../../common/arr-modal/ArrModal';
import {useReviewHeaderContext} from '../../../header/ReviewHeader';
import {ToastType} from '../../../../../../shared/constants';
import {useUploadProtocolIntelligence} from '../../../../../../hooks/ai/review/useReview';
import {t} from 'i18next';
import {Box, Typography} from '@mui/material';
import {ReactComponent as UploadIcon} from '../../../../../../assets/images/upload.svg';
import ArrTextField from '../../../../../common/arr-text-field/ArrTextField';
import {
  getProtocolNameValidationError,
  ProtocolNameValidationError,
} from '../../../common/JobDetails/SaveAsNewProtocolModal';

type UploadValidationError = 'INVALID_TYPE' | 'FILE_TOO_LARGE';
const duplicateFileNameCode = '94001000025';

interface UploadButtonProps {
  children: ReactNode;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  ariaLabel?: string;
  tabIndex?: number;
  dataTestId?: string;
}

const accept = '.doc,.docx,.pdf';
const allowedFileTypes = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/pdf',
];
const MAX_FILE_SIZE = 500 * 1024 * 1024;
const MAX_FILE_NAME = 50;

const UploadButton = ({
  children,
  className,
  size = 'medium',
  ariaLabel,
  tabIndex,
  dataTestId,
}: UploadButtonProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const {
    setReviewHeaderToast,
    handlReviewHeaderToastClose,
    clearSearchAndFetchProtocolList,
  } = useReviewHeaderContext();

  const [openUploadErrorModal, setOpenUploadErrorModal] = useState(false);
  const [isFailedDuplicateUploadModal, setIsFailedDuplicateUploadModal] =
    useState(false);
  const [fileSizeError, setFileSizeError] = useState(false);
  const [fileNameError, setFileNameError] = useState(false);
  const [customFileName, setCustomFileName] = useState('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const {mutate: uploadProtocolIntelligence} = useUploadProtocolIntelligence();
  const [
    protocolNameValidationError,
    setProtocolNameValidationValidationError,
  ] = useState<ProtocolNameValidationError | null>(null);

  const handleValidationError = (errorType: UploadValidationError) => {
    setFileSizeError(errorType === 'FILE_TOO_LARGE');
    setOpenUploadErrorModal(true);
    handlReviewHeaderToastClose();
  };

  const handleFileUpload = (file: File) => {
    uploadProtocolIntelligence(
      {file},
      {
        onSuccess: () => {
          setReviewHeaderToast({
            isOpen: true,
            message: t('ai.review.protocolIntelligence.uploadFileToastSuccess'),
            status: ToastType.Success,
          });
          clearSearchAndFetchProtocolList();
          handleErrorModalClose();
        },
        onError: (error: any) => {
          handleErrorModalClose();
          if (error?.status === 409) {
            if (error?.response?.data?.detail?.includes(duplicateFileNameCode))
              setIsFailedDuplicateUploadModal(true);
          } else {
            setReviewHeaderToast({
              isOpen: true,
              message:
                error?.response?.data?.detail ??
                t('ai.review.protocolIntelligence.uploadFileToastFailure'),
              status: ToastType.Error,
            });
          }
        },
      }
    );
  };

  const handleErrorModalClose = () => {
    setIsFailedDuplicateUploadModal(false);
    setFileSizeError(false);
    setOpenUploadErrorModal(false);
    setFileNameError(false);
    setPendingFile(null);
    setCustomFileName('');
    setFileNameError(false);
  };

  const handleUploadClick = () => {
    handleErrorModalClose();
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (allowedFileTypes.length > 0 && !allowedFileTypes.includes(file.type)) {
      handleValidationError('INVALID_TYPE');
      event.target.value = '';
      return;
    }
    if (MAX_FILE_SIZE && file.size > MAX_FILE_SIZE) {
      handleValidationError('FILE_TOO_LARGE');
      event.target.value = '';
      return;
    }
    if (file.name.length > MAX_FILE_NAME) {
      setPendingFile(file);
      setFileNameError(true);
      event.target.value = '';
      return;
    }
    handleFileUpload(file);
    // Allows selecting the same file again
    event.target.value = '';
  };

  const handleNewFileNameChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const {value = ''} = event.target;
    if (value.length <= MAX_FILE_NAME) {
      const error = getProtocolNameValidationError(value, [], true);
      setProtocolNameValidationValidationError(error);
      setCustomFileName(value);
    }
  };

  const handleSaveAndUpload = () => {
    if (!pendingFile) return;
    const extension = pendingFile.name.includes('.')
      ? `.${pendingFile.name.split('.').pop()}`
      : '';
    const renamedFile = new File(
      [pendingFile],
      `${customFileName}${extension}`,
      {
        type: pendingFile.type,
        lastModified: pendingFile.lastModified,
      }
    );
    handleFileUpload(renamedFile);
  };

  const getValidationMessage = (
    error: ProtocolNameValidationError | null
  ): string | undefined => {
    if (error === 'specialChar') {
      return t('ai.review.jobDetailsTab.saveAsNewProtocolSpecialCharError');
    }
    return undefined;
  };

  const helperText = useMemo(() => {
    const errorMessage = getValidationMessage(protocolNameValidationError);
    if (errorMessage) {
      return errorMessage;
    }
    return `${customFileName.length}/${MAX_FILE_NAME}`;
  }, [customFileName.length, protocolNameValidationError]);

  return (
    <>
      <ArrButton
        className={className}
        onClick={handleUploadClick}
        size={size}
        variant="primary"
        aria-label={ariaLabel}
        tabIndex={tabIndex}
        dataTestId={dataTestId ?? 'upload-button'}
        startIcon={<UploadIcon />}
      >
        {children}
      </ArrButton>

      <input
        hidden
        type="file"
        ref={fileInputRef}
        accept={accept}
        onChange={handleFileChange}
      />
      {openUploadErrorModal && (
        <ArrModal
          showClose={false}
          showModal={openUploadErrorModal}
          onClose={handleErrorModalClose}
          title={
            fileSizeError
              ? t(
                  'ai.review.protocolIntelligence.uploadErrorModalFileSizeTitle'
                )
              : t('ai.review.protocolIntelligence.uploadErrorModalTitle')
          }
          aria-label="upload-protocol-error-modal"
          dataTestId="arr-upload-protocol-error-modal"
          buttons={[
            {
              id: t('common.close'),
              label: t('common.close'),
              onClick: handleErrorModalClose,
              variant: 'secondary',
              dataTestId: 'modal-no',
            },
            {
              id: t('common.tryAgain'),
              label: t('common.tryAgain'),
              onClick: handleUploadClick,
              variant: 'primary',
              dataTestId: 'modal-yes',
            },
          ]}
        >
          <Typography
            className="upload-protocol-error-description"
            tabIndex={0}
            data-testid={`upload-protocol-description${
              fileSizeError ? '-error' : ''
            }`}
          >
            {fileSizeError
              ? t(
                  'ai.review.protocolIntelligence.uploadErrorModalFileSizeDescription'
                )
              : t('ai.review.protocolIntelligence.uploadErrorModalDescription')}
          </Typography>
        </ArrModal>
      )}
      <ArrModal
        title={t('ai.review.protocolIntelligence.protocolFileNameModalTitle')}
        showModal={fileNameError}
        onClose={() => setFileNameError(false)}
        showClose={false}
        dataTestId="failed-fileName-protocol-modal"
        buttons={[
          {
            id: t('common.close'),
            label: t('common.close'),
            onClick: () => setFileNameError(false),
            dataTestId: 'fn-modal-no',
            variant: 'secondary',
          },
          {
            id: t('ai.review.protocolIntelligence.saveAndUpload'),
            label: t('ai.review.protocolIntelligence.saveAndUpload'),
            onClick: handleSaveAndUpload,
            variant: 'primary',
            dataTestId: 'fn-modal-yes',
            disabled:
              Boolean(protocolNameValidationError) ||
              Boolean(customFileName.length === 0),
          },
        ]}
        className="delete-protocol-modal"
      >
        <Box className="delete-protocol-modal-description">
          <Typography
            className="typo-text body2_Size text-body-strong"
            data-testid="approved-protocol-actions-p1"
          >
            {t(
              'ai.review.protocolIntelligence.protocolFileNameModalDescription'
            )}
          </Typography>
        </Box>
        <Box>
          <ArrTextField
            label={t(
              'ai.review.protocolIntelligence.protocolFileNameModalInputLabel'
            )}
            value={customFileName}
            error={Boolean(protocolNameValidationError)}
            state={protocolNameValidationError ? 'error' : 'enabled'}
            showErrorIcon={Boolean(protocolNameValidationError)}
            onChange={handleNewFileNameChange}
            dataTestId="new-file-name-input"
            className="mt-16"
            supportText={helperText}
            helperText={helperText}
            slotProps={{
              input: {
                inputProps: {maxLength: MAX_FILE_NAME},
              },
              formHelperText: {
                'aria-label': protocolNameValidationError
                  ? getValidationMessage(protocolNameValidationError)
                  : `${customFileName.length} ${t('ai.review.jobDetailsTab.saveAsNewProtocolOutOf50Characters')}`,
              },
            }}
          />
        </Box>
      </ArrModal>
      {isFailedDuplicateUploadModal && (
        <ArrModal
          title={t('ai.review.protocolIntelligence.uploadDuplicateFailedTitle')}
          showModal={isFailedDuplicateUploadModal}
          onClose={() => setIsFailedDuplicateUploadModal(false)}
          showClose={false}
          dataTestId="failed-delete-protocol-modal"
          buttons={[
            {
              id: t('common.close'),
              label: t('common.close'),
              onClick: () => setIsFailedDuplicateUploadModal(false),
              dataTestId: 'dp-close-delete',
            },
          ]}
          className="delete-protocol-modal"
        >
          <Box className="delete-protocol-modal-description">
            <Typography
              className="typo-text body2_Size text-body-strong"
              data-testid="approved-protocol-actions-p1"
            >
              {t(
                'ai.review.protocolIntelligence.uploadDuplicateFailedDescription'
              )}
            </Typography>
          </Box>
        </ArrModal>
      )}
    </>
  );
};

export default UploadButton;
